import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryConfig';

/**
 * Hook that prefetches cross-feature data based on navigation
 * Improves perceived performance by loading data before users need it
 */
export function useNavigationPreload() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const prefetchForRoute = async () => {
      const path = location.pathname;

      if (path === '/') {
        // Dashboard: prefetch consolidated personality (assessment + overrides)
        queryClient.prefetchQuery({
          queryKey: ['consolidated-personality', user.id],
          queryFn: async () => {
            const [assessmentResult, overridesResult] = await Promise.all([
              supabase
                .from('assessments')
                .select('id, profile, variant, updated_at')
                .eq('user_id', user.id)
                .neq('source_type', 'integral_session')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle(),
              supabase
                .from('personality_overrides')
                .select('*')
                .eq('user_id', user.id)
                .maybeSingle()
            ]);
            return { 
              assessment: assessmentResult.data, 
              overrides: overridesResult.data 
            };
          },
          staleTime: 5 * 60 * 1000,
        });

        // Liker inference for cross-feature insights
        queryClient.prefetchQuery({
          queryKey: queryKeys.liker.inference(user.id),
          queryFn: async () => {
            const { data } = await supabase
              .from('liker_personality_inference')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });

        // Unified personality insights
        queryClient.prefetchQuery({
          queryKey: queryKeys.insights.unified(user.id),
          queryFn: async () => {
            const { data } = await supabase
              .from('unified_personality_insights')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });
      } else if (path === '/profile') {
        // Profile page: prefetch privacy settings
        queryClient.prefetchQuery({
          queryKey: queryKeys.privacy.user(user.id),
          queryFn: async () => {
            const { data } = await supabase
              .from('privacy_settings')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 10 * 60 * 1000,
        });
      } else if (path.startsWith('/liker')) {
        // Liker needs TPS assessment for personality context
        queryClient.prefetchQuery({
          queryKey: queryKeys.assessments.user(user.id),
          queryFn: async () => {
            const { data } = await supabase
              .from('assessments')
              .select('id, profile, variant, updated_at')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false })
              .limit(1);
            return data;
          },
          staleTime: 10 * 60 * 1000,
        });

        // Also prefetch media preferences
        queryClient.prefetchQuery({
          queryKey: queryKeys.liker.preferences(user.id),
          queryFn: async () => {
            const { data } = await supabase
              .from('liker_media_preferences')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });
      } else if (path === '/quizzes') {
        // Quizzes need Liker inference for character matching
        queryClient.prefetchQuery({
          queryKey: queryKeys.liker.inference(user.id),
          queryFn: async () => {
            const { data } = await supabase
              .from('liker_personality_inference')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });
      }
    };

    prefetchForRoute();
  }, [location.pathname, queryClient, user]);
}
